import React from "react";
import { Snowflake, MessageCircle, Thermometer, Truck, Refrigerator } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import SafeImage from "@/components/SafeImage";
import { COLD_CHAIN_PRODUCTS, COLD_CHAIN_CATEGORIES } from "@/data/coldChain";
import { buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";
import { BAH_PRODUCT_CATALOGUE_PDF } from "@/lib/assets";

const CAT_ICON = {
  refrigerators: Refrigerator,
  carriers: Truck,
  monitoring: Thermometer,
};

export default function ColdChain() {
  return (
    <div data-testid="cold-chain-page">
      <PageHero
        testId="coldchain-hero"
        eyebrow="Cold Chain & Vaccine Support"
        title="Vaccines deserve a cold chain that actually holds."
        lede="Vaccine refrigerators, carriers and electronic temperature loggers — for clinics, institutions and outreach campaigns across East Africa."
        image="https://images.pexels.com/photos/162240/bull-calf-heifer-ko-162240.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "WhatsApp Cold-Chain Team",
          href: buildWaLink(WA_TEMPLATES.cold_chain),
          external: true,
        }}
        secondaryCta={{
          label: "Download Catalogue",
          href: BAH_PRODUCT_CATALOGUE_PDF,
          external: true,
        }}
      />

      <section className="py-16 md:py-24 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {COLD_CHAIN_CATEGORIES.map((c) => {
            const items = COLD_CHAIN_PRODUCTS.filter((p) => p.category === c.id);
            const Icon = CAT_ICON[c.id] || Snowflake;
            return (
              <div key={c.id} data-testid={`coldchain-group-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-bah-green/10 grid place-content-center text-bah-green">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <SectionLabel>{c.label}</SectionLabel>
                    <h2 className="mt-2 font-display text-2xl md:text-3xl text-bah-ink">
                      {c.label}
                    </h2>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {items.map((p) => (
                    <article
                      key={p.id}
                      className="card-rise bg-white border border-bah-line rounded-2xl p-6 flex flex-col"
                      data-testid={`coldchain-card-${p.id}`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.22em] text-bah-green font-bold">
                        {p.type}
                      </div>
                      <h3 className="mt-2 font-display text-xl text-bah-ink leading-snug">
                        {p.name}
                      </h3>
                      <p className="mt-3 text-sm text-bah-mute leading-relaxed">{p.short}</p>
                      <div className="mt-4 text-sm bg-bah-paperAlt rounded-lg p-3 border border-bah-line text-bah-ink">
                        {p.field_note}
                      </div>
                      <div className="mt-auto pt-4">
                        <a
                          href={buildWaLink(`Hello Biodeal Animal Health, please share specifications and pricing for ${p.name}.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full bg-bah-green text-white text-sm font-semibold px-4 py-2.5 hover:bg-bah-greenLight"
                          data-testid={`coldchain-cta-${p.id}`}
                        >
                          <MessageCircle className="h-4 w-4" /> Request Specifications
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 bg-bah-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionLabel>Beyond the hardware</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl leading-tight">
              Cold chain that holds, from clinic to last mile.
            </h2>
            <p className="mt-5 text-white/75 md:text-lg max-w-lg">
              Pair refrigerators, carriers and electronic loggers with training
              on cold-chain practice. The Biodeal Animal Health team can advise
              on the right configuration for your clinic, institution or
              vaccination campaign.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={buildWaLink(WA_TEMPLATES.cold_chain)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
                data-testid="coldchain-final-whatsapp"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Cold-Chain Team
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              "https://images.pexels.com/photos/162240/bull-calf-heifer-ko-162240.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
              "https://images.pexels.com/photos/6235241/pexels-photo-6235241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
              "https://images.pexels.com/photos/1300357/pexels-photo-1300357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
              "https://images.pexels.com/photos/422218/pexels-photo-422218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
            ].map((src, i) => (
              <div
                key={i}
                className={`relative overflow-hidden rounded-2xl border border-white/10 ${
                  i % 2 === 1 ? "translate-y-4" : ""
                }`}
              >
                <SafeImage
                  src={src}
                  alt="Cold chain activity"
                  loading="lazy"
                  className="w-full h-full object-cover aspect-square"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
